import { motion } from "framer-motion";
import { Check } from "lucide-react";

const BULLETS = [
  { t: "Beginner-friendly", d: "Zero prior experience required. We assume you're starting at day one." },
  { t: "Easy-to-understand", d: "Plain English, with charts and analogies — not finance-bro jargon." },
  { t: "Step-by-step", d: "Every chapter builds on the last. Read in order, skip nothing." },
  { t: "Like a mini course", d: "Organized into self-contained lessons with worksheets to apply each idea." },
];

export default function WhyBuy() {
  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[4/5] lg:aspect-[5/6]"
          >
            <img
              src="https://images.unsplash.com/photo-1520583457224-aee11bad5112?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
              alt="Dark trading workspace"
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="text-emerald-400 text-xs tracking-[0.3em] uppercase font-display font-semibold mb-2">
                Built for real life
              </div>
              <h3 className="font-display font-bold text-white text-2xl md:text-3xl tracking-tight">
                Read it in your evenings.<br />
                Apply it the next morning.
              </h3>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="text-emerald-400 uppercase tracking-[0.3em] text-xs font-semibold font-display mb-4">
              Why this guide
            </div>
            <h2 className="font-display font-bold text-white text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
              Not another <span className="line-through text-white/30">guru course</span>.<br />
              A guide built like a <span className="text-emerald-400">real book.</span>
            </h2>
            <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed">
              Most trading content is built to keep you watching. This guide is built to actually
              teach you — in the shortest path possible.
            </p>

            <ul className="mt-10 space-y-5">
              {BULLETS.map((b) => (
                <li key={b.t} className="flex gap-4">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/30 grid place-items-center mt-0.5">
                    <Check className="w-4 h-4 text-emerald-400" />
                  </span>
                  <div>
                    <div className="font-display font-semibold text-white">{b.t}</div>
                    <div className="text-white/55 text-sm md:text-base">{b.d}</div>
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
